import {
	Box,
	Grid,
	Stack,
	Typography,
	useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { memo, useMemo } from 'react';
import strings from '../../Globalization/index';
import consts from '../../consts';
import { useCycleInfo } from '../../contexts/CycleInfo/Context';
import connectionService from '../../services/Connection';
import { customTopSectionInputComponentMapper } from '../endosystemcycleinfoviews/TopSection';

const TopSection = () => {
	const { data, clientId = 0, onOpenCIResultFormDialog } = useCycleInfo();
	const theme = useTheme();
	const screen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const isEnspire = data.EquipmentFamilyId === consts.EquipmentFamilyType.ENSPIRE;
	const rows = data.TopSection;
	const endoScopeTop = useMemo(() => rows.find((row) => row.name === 'endoScopeTop'), [rows])
	const endoScopeBottomItems = useMemo(() => rows.find((row) => row.name === 'endoScopeBottom')?.entries || [], [rows]);
	const cycleItems = useMemo(() => rows.find((row) => row.name === 'cycle')?.entries || [], [rows]);
	const reviewItems = useMemo(() => rows.find((row) => row.name === 'review')?.entries || [], [rows]);
	const endoEnspireTop = useMemo(() => rows.find((row) => row.name === 'endoEnspireTop') || { entries: [], noOfColumns: 1 }, [rows]);
	const endoEnspireBottom = useMemo(
		() => rows.find((row) => row.name === 'endoEnspireBottom') || { entries: [], noOfColumns: 1 },
		[rows]
	);

	return (
		<Grid
			container
			sx={{
				'& .top-section-grid': {
					fontSize: 'inherit',
					display: 'grid',
					gridTemplateColumns: screen ? '1fr 1em 1fr 1fr 1em 1fr' : '1fr 1em 1fr 1fr 1em 1fr 1fr 1em 1fr',
					gap: '3px 5px',
					'& .LabelBold': {
						fontWeight: 'bold'
					},
					'& *': {
						msWordBreak: 'break-all',
						wordBreak: ['break-all', 'break-word'],
						WebkitHyphens: 'auto',
						MozHyphens: 'auto',
						msHyphens: 'auto',
						hyphens: 'auto'
					}
				}
			}}
		>
			<Grid
				size={{ xs: 12, md: 12, lg: 3 }}
				sx={{
					mt: theme.spacing(),
					padding: theme.spacing()
				}}
			>
				<Stack>
					<Box className="washer">
						<Box
							component="img"
							src={`${connectionService.PTXImage}/${data.CycleImageUrl}?v=${new Date()}`}
							alt={data.EquipmentName}
							sx={{ height: 'auto', width: theme.spacing(9) }}
							onError={(e) => {
								e.target.src = `${connectionService.PTXImage}/${'EquipmentType/' + clientId}/${data?.EquipmentTypeId + '.png'
									}?v=${new Date()}`;
							}}
						/>
					</Box>
					{!!data?.EquipmentName && (
						<Typography
							className="washer-serialNumber"
							variant="subtitle1"
							fontWeight={'bold'}
							title={strings.EquipmentName}
							component="span"
						>
							{data.EquipmentName}
						</Typography>
					)}
					{!!data?.ModelName && (
						<Typography
							className="washer-serialNumber"
							variant="subtitle2"
							fontWeight={'bold'}
							title={strings.ModalName}
							component="span"
						>
							{data.ModelName}
						</Typography>
					)}
					{!!data?.SerialNumber && (
						<Typography
							className="washer-serialNumber"
							variant="subtitle1"
							fontWeight={'bold'}
							title={strings.SerialNumber}
							component="span"
						>
							{data.SerialNumber === '-1' ? '' : data.SerialNumber}
						</Typography>
					)}
				</Stack>
			</Grid>
			<Grid size={{ xs: 12, md: 12, lg: 9 }} sx={{ mt: theme.spacing(), padding: theme.spacing(), fontSize: 12 }}>
				<Grid size={12} className="top-section-grid">
					{cycleItems.reduce(
						(acc, { HeaderLabel, Name, Value, element }, index) =>
							acc.concat([
								<Typography
									component={'span'}
									className="LabelBold"
									fontSize={'inherit'}
									key={`label-${index}`}
								>
									{strings[HeaderLabel] || strings[Name]}
								</Typography>,
								<Typography
									variant="body2"
									component={'span'}
									fontSize={'inherit'}
									className="LabelBold"
									key={`colon-${index}`}
								>
									:
								</Typography>,
								<Typography component={'span'} fontSize={'inherit'} key={`value-${index}`}>
									{element || Value}
								</Typography>
							]),
						[]
					)}
				</Grid>
				{!isEnspire && endoScopeTop?.entries.length > 0 && (
					<Grid size={12} container pt={1} pb={1}>
						<Grid size={12}>
							<Typography component="body2" fontWeight={'bold'} fontSize={'inherit'} gutterBottom>
								{strings.EndoScopeTop}
							</Typography>
						</Grid>
						<Grid
							size={12}
							className="top-section-grid"
							sx={{
								gridTemplateColumns: `${endoScopeTop?.entries?.length % 4 === 0
									? '1fr 1em 1fr 1fr 1em 1fr 1fr 1em 1fr 1fr 1em 1fr '
									: '1fr 1em 1fr 1fr 1em 1fr 1fr 1em 1fr'
									} !important`
							}}
						>
							{endoScopeTop?.entries.reduce(
								(acc, { HeaderLabel, Name, DataProperty, Value, element }, index) =>
									acc.concat([
										<Typography
											component={'span'}
											className="LabelBold"
											fontSize={'inherit'}
											title={DataProperty}
											key={`label-${index}`}
										>
											{strings[HeaderLabel] || strings[Name]}
										</Typography>,
										<Typography
											variant="body2"
											component={'span'}
											fontSize={'inherit'}
											className="LabelBold"
											key={`colon-${index}`}
										>
											:
										</Typography>,
										<Typography component={'span'} fontSize={'inherit'} key={`value-${index}`}>
											{element || Value}
										</Typography>
									]),
								[]
							)}
						</Grid>
					</Grid>
				)}
				{!isEnspire && endoScopeBottomItems.length > 0 && (
					<Grid size={12} container pt={1} pb={1}>
						<Grid size={12}>
							<Typography component="body2" fontWeight={'bold'} fontSize={'inherit'} gutterBottom>
								{strings.EndoScopeBottom}
							</Typography>
						</Grid>
						<Grid
							size={12}
							className="top-section-grid"
							sx={{
								gridTemplateColumns: `${endoScopeBottomItems.length % 4 === 0
									? '1fr 1em 1fr 1fr 1em 1fr 1fr 1em 1fr 1fr 1em 1fr '
									: '1fr 1em 1fr 1fr 1em 1fr 1fr 1em 1fr'
									} !important`
							}}
						>
							{endoScopeBottomItems.reduce(
								(acc, { HeaderLabel, Name, DataProperty, Value, element }, index) =>
									acc.concat([
										<Typography
											component={'span'}
											className="LabelBold"
											fontSize={'inherit'}
											title={DataProperty}
											key={`label-${index}`}
										>
											{strings[HeaderLabel] || strings[Name]}
										</Typography>,
										<Typography
											variant="body2"
											component={'span'}
											fontSize={'inherit'}
											className="LabelBold"
											key={`colon-${index}`}
										>
											:
										</Typography>,
										<Typography component={'span'} fontSize={'inherit'} key={`value-${index}`}>
											{element || Value}
										</Typography>
									]),
								[]
							)}
						</Grid>
					</Grid>
				)}
				{isEnspire && endoEnspireTop?.entries?.length > 0 && (
					<Grid size={12} container pt={1} pb={1}>
						<Grid size={12}>
							<Typography component="body2" fontWeight={'bold'} fontSize={'inherit'} gutterBottom>
								{strings.EndoScopeTop}
							</Typography>
						</Grid>
						<Grid
							size={12}
							className="top-section-grid"
							sx={{
								gridTemplateColumns: `${'1fr 1em 1fr '.repeat(endoEnspireTop.noOfColumns)} !important`,
								pl: 3
							}}
						>
							{endoEnspireTop?.entries?.reduce(
								(acc, { HeaderLabel, Name, DataProperty, Value, element }, index) =>
									acc.concat([
										<Typography
											component={'span'}
											className="LabelBold"
											fontSize={'inherit'}
											key={`label-${index}`}
										>
											{strings[HeaderLabel] || strings[Name]}
										</Typography>,
										<Typography
											variant="body2"
											component={'span'}
											fontSize={'inherit'}
											className="LabelBold"
											key={`colon-${index}`}
										>
											:
										</Typography>,
										<Typography component={'span'} fontSize={'inherit'} key={`value-${index}`}>
											{element || Value !== 'undefined' ? Value : ''}
										</Typography>
									]),
								[]
							)}
						</Grid>
					</Grid>
				)}

				{isEnspire && endoEnspireBottom?.entries?.length > 0 && (
					<Grid size={12} container pt={1} pb={1}>
						<Grid size={12}>
							<Typography component="body2" fontWeight={'bold'} fontSize={'inherit'} gutterBottom>
								{strings.EndoScopeBottom}
							</Typography>
						</Grid>
						<Grid
							size={12}
							className="top-section-grid"
							sx={{
								gridTemplateColumns: `${'1fr 1em 1fr '.repeat(endoEnspireBottom.noOfColumns)} !important`,
								pl: 3
							}}
						>
							{endoEnspireBottom?.entries?.reduce(
								(acc, { HeaderLabel, Name, DataProperty, Value, element }, index) =>
									acc.concat([
										<Typography
											component={'span'}
											className="LabelBold"
											fontSize={'inherit'}
											key={`label-${index}`}
										>
											{strings[HeaderLabel] || strings[Name]}
										</Typography>,
										<Typography
											variant="body2"
											component={'span'}
											fontSize={'inherit'}
											className="LabelBold"
											key={`colon-${index}`}
										>
											:
										</Typography>,
										<Typography component={'span'} fontSize={'inherit'} key={`value-${index}`}>
											{element || Value !== 'undefined' ? Value : ''}
										</Typography>
									]),
								[]
							)}
						</Grid>
					</Grid>
				)}
				<Grid size={12} className="top-section-grid">
					{reviewItems.reduce((acc, { HeaderLabel, Name, DataProperty, Value, element }, index) => {
						const CustomInputComponent = customTopSectionInputComponentMapper[DataProperty];

						return acc.concat([
							<Typography
								component="span"
								className="LabelBold"
								fontSize="inherit"
								key={`label-${index}`}
							>
								{strings[HeaderLabel] || strings[Name]}
							</Typography>,
							<Typography
								variant="body2"
								component="span"
								fontSize="inherit"
								className="LabelBold"
								key={`colon-${index}`}
							>
								:
							</Typography>,
							CustomInputComponent ? (
								<CustomInputComponent
									key={index}
									CriticalParameterIcon={Value}
									align='left'
									ml={0}
									data={data}
									dataProperty={DataProperty}
									Value={Value}
									handleFormOpen={onOpenCIResultFormDialog}
								/>
							) : (
								<Typography component="span" fontSize="inherit" key={`value-${index}`}>
									{element ? element : Value || data[DataProperty]}
								</Typography>
							)
						]);
					}, [])}
				</Grid>
			</Grid>
		</Grid>
	);
};
export default memo(TopSection);
